import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { useSheinPageShopScope } from '@/shein-platform/hooks/useSheinPageShopScope';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import { showToast } from '@/utils/toast';

function normalizeTree(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.info)) return res.info;
  return [];
}

export default function EnvironmentalMaterialsPanel({
  syncKinds = ['environmental'],
} = {}) {
  const { t } = useTranslation('workbench');
  const {
    activeShop,
    activeShopId,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const shopId = concreteScopeId || (allShopsScope ? '' : (activeShop?.id || activeShopId));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  const fetchTree = useCallback(async () => {
    if (!shopId) {
      setRows([]);
      return [];
    }
    const res = await sheinPlatformAPI.listEnvironmentalMaterials(shopId);
    const list = normalizeTree(res);
    setRows(list);
    if (res?.lastSyncedAt) {
      setLastSyncedAt(String(res.lastSyncedAt));
    }
    return list;
  }, [shopId]);

  const load = useCallback(async () => {
    if (!shopId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      await fetchTree();
    } catch (e) {
      setRows([]);
      showToast(e?.message || t('compliance.loadEnvFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, fetchTree, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setRows([]);
    setLastSyncedAt('');
    setKeyword('');
  }, [shopId]);

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: 'environmental',
        shopId,
        label: t('publishRecord.syncUpdate'),
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      await fetchTree();
      setLastSyncedAt(new Date().toISOString());
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      showToast(e?.message || t('productAction.syncFailed'), 'error');
    }
  }, [shopId, startJob, onTrackDone, fetchTree, t]);

  const { refresh: onSync, refreshing: syncingLocal, cooldownSeconds: syncCooldown } = useGuardedRefresh(
    handleSync,
    { minIntervalMs: 5000 },
  );

  const syncing = syncingLocal || isBusy('environmental', shopId);

  const latestSyncedLabel = lastSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(lastSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return rows;
    return rows
      .map((type) => {
        const materials = Array.isArray(type?.packageMaterials) ? type.packageMaterials : [];
        const matchedMaterials = materials.filter((m) => {
          const name = String(m?.packageMaterialName || '').toLowerCase();
          const id = String(m?.packageMaterialId || '');
          return name.includes(q) || id.includes(q);
        });
        const typeName = String(type?.packageTypeName || '').toLowerCase();
        const typeHit = typeName.includes(q) || String(type?.packageTypeId || '').includes(q);
        if (!typeHit && matchedMaterials.length === 0) return null;
        return {
          ...type,
          packageMaterials: typeHit ? materials : matchedMaterials,
        };
      })
      .filter(Boolean);
  }, [rows, keyword]);

  const flattenMaterials = (typeList) => {
    const exportRows = [];
    (Array.isArray(typeList) ? typeList : []).forEach((type) => {
      const materials = Array.isArray(type.packageMaterials) ? type.packageMaterials : [];
      if (materials.length === 0) {
        exportRows.push({
          packageTypeId: type.packageTypeId ?? '',
          packageTypeName: type.packageTypeName || '',
          typeEnabled: Number(type.isEnabled) !== 0 ? t('brandLib.available') : t('compliance.disabled'),
          packageMaterialId: '',
          packageMaterialName: '',
          materialEnabled: '',
        });
        return;
      }
      materials.forEach((m) => {
        exportRows.push({
          packageTypeId: type.packageTypeId ?? '',
          packageTypeName: type.packageTypeName || '',
          typeEnabled: Number(type.isEnabled) !== 0 ? t('brandLib.available') : t('compliance.disabled'),
          packageMaterialId: m.packageMaterialId ?? '',
          packageMaterialName: m.packageMaterialName || '',
          materialEnabled: Number(m.isEnabled) === 0 ? t('compliance.disabled') : t('brandLib.available'),
        });
      });
    });
    return exportRows;
  };

  const writeMaterialsCsv = (typeList, filename) => {
    const exportRows = flattenMaterials(typeList);
    if (!exportRows.length) {
      showToast(t('compliance.noneEnv'), 'warning');
      return;
    }
    downloadCsv(
      filename,
      ['packageTypeId', 'packageTypeName', 'typeEnabled', 'packageMaterialId', 'packageMaterialName', 'materialEnabled'],
      exportRows,
    );
    showToast(t('compliance.exportedEnv', { count: exportRows.length }), 'success');
  };

  const exportFiltered = () => {
    writeMaterialsCsv(filtered, `shein-environmental-materials-filtered-${Date.now()}.csv`);
  };

  const exportPage = () => {
    writeMaterialsCsv(filtered, `shein-environmental-materials-page-${Date.now()}.csv`);
  };

  return (
    <>
      <SheinSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            busy={syncing || loading}
            syncKinds={syncKinds}
            quotaKind="environmental"
            onSync={() => {
              if (!shopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
                return;
              }
              onSync();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={loading && !lastSyncedAt ? t('returnList.loading') : latestSyncedLabel}
            syncHint={t('compliance.envSyncHint', { platform: 'SHEIN' })}
            exportLabel={opsExportLabel(false)}
            exportDisabled={!shopId || filtered.length === 0}
            exportMenu={buildOpsExportMenu({
              filteredCount: flattenMaterials(filtered).length,
              pageCount: flattenMaterials(filtered).length,
              selectedCount: 0,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
            })}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--grow">
              <span className="spo-field__label">{t('compliance.keyword')}</span>
              <input
                className="spo-input"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={t('compliance.keywordPh')}
                disabled={!shopId}
              />
            </div>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard tabs={shopDimensionTabs}>
        {!shopId ? (
          <p className="spo-empty-hint">{allShopsScope ? t('draftBox.pickConcrete') : t('returnList.pickShop')}</p>
        ) : loading && rows.length === 0 ? (
          <p className="spo-empty-hint">{t('returnList.loading')}</p>
        ) : filtered.length === 0 ? (
          <p className="spo-empty-hint">
            {lastSyncedAt ? t('compliance.noMatchEnv') : t('compliance.emptyEnv', { platform: 'SHEIN' })}
          </p>
        ) : (
          <div className="spo-env-tree-wrap">
            <div className="spo-env-tree">
              {filtered.map((type) => {
                const materials = Array.isArray(type.packageMaterials) ? type.packageMaterials : [];
                const enabled = Number(type.isEnabled) !== 0;
                return (
                  <section key={String(type.packageTypeId || type.packageTypeName)} className="spo-env-tree__type">
                    <header className="spo-env-tree__type-head">
                      <h3>{type.packageTypeName || t('compliance.typeFallback', { n: type.packageTypeId })}</h3>
                      <span className={`spo-status spo-status--${enabled ? 'success' : 'failed'}`}>
                        {enabled ? t('brandLib.available') : t('compliance.disabled')}
                      </span>
                      <span className="spo-muted">ID {type.packageTypeId ?? '—'}</span>
                    </header>
                    <ul className="spo-env-tree__materials">
                      {materials.map((m) => (
                        <li key={String(m.packageMaterialId || m.packageMaterialName)}>
                          <strong>{m.packageMaterialName || '—'}</strong>
                          <span className="spo-muted">#{m.packageMaterialId ?? '—'}</span>
                          {Number(m.isEnabled) === 0 ? (
                            <span className="spo-status spo-status--failed">{t('compliance.disabled')}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          </div>
        )}
      </ProductListCard>
    </>
  );
}
